import {
  Badge,
  Box,
  Button,
  Dialog,
  HStack,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Wallet } from "lucide-react";

interface WithdrawalDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  withdrawal: {
    id: string;
    createdAt: string;
    amount: number;
    totalFee: number;
    status: "pending" | "processing" | "completed" | "cancelled" | "declined";
    account: string;
  } | null;
  isLoading: boolean;
}

export default function WithdrawalDetailModal({
  isOpen,
  onClose,
  withdrawal,
  isLoading,
}: WithdrawalDetailModalProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat("es-CO", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(dateString));
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge colorPalette="orange">Pendiente</Badge>;
      case "processing":
        return <Badge colorPalette="blue">En proceso</Badge>;
      case "completed":
        return <Badge colorPalette="green">Completado</Badge>;
      case "cancelled":
        return <Badge colorPalette="red">Cancelado</Badge>;
      case "declined":
        return <Badge colorPalette="red">Rechazado</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <Dialog.Root
      lazyMount
      open={isOpen}
      onOpenChange={(e) => !e.open && onClose()}
      size="md"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header borderBottom="1px solid #ebecef80" p={4}>
              <Dialog.Title fontSize="lg" fontWeight="semibold">
                Detalle del retiro
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger position="top-end" top={3} />
            <Dialog.Body py={6}>
              {isLoading ? (
                <Text color="gray.500">Cargando...</Text>
              ) : withdrawal ? (
                <VStack gap={4} align="stretch">
                  <Box
                    p={4}
                    bg="gray.50"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <HStack justify="space-between" align="center">
                      <HStack gap={3}>
                        <Box p={2} bg="palomma.100" borderRadius="md">
                          <Wallet size={20} color="#8e5cff" />
                        </Box>
                        <VStack align="start" gap={0}>
                          <Text fontSize="sm" color="gray.500">
                            Monto solicitado
                          </Text>
                          <Text
                            fontSize="xl"
                            fontWeight="bold"
                            color="gray.800"
                          >
                            {formatCurrency(withdrawal.amount)}
                          </Text>
                        </VStack>
                      </HStack>
                      {getStatusBadge(withdrawal.status)}
                    </HStack>
                  </Box>

                  <VStack align="stretch" gap={2}>
                    <HStack justify="space-between">
                      <Text color="gray.500" fontSize="sm">
                        Fecha de solicitud
                      </Text>
                      <Text fontWeight="medium">
                        {formatDate(withdrawal.createdAt)}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.500" fontSize="sm">
                        Comisión
                      </Text>
                      <Text fontWeight="medium">
                        {formatCurrency(withdrawal.totalFee)}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.500" fontSize="sm">
                        Monto descontado
                      </Text>
                      <Text fontWeight="bold">
                        {formatCurrency(
                          withdrawal.amount + withdrawal.totalFee
                        )}
                      </Text>
                    </HStack>
                    <HStack
                      justify="space-between"
                      pt={2}
                      borderTop="1px solid"
                      borderColor="gray.200"
                    >
                      <Text color="gray.500" fontSize="sm">
                        Cuenta destino
                      </Text>
                      <Text fontWeight="medium">{withdrawal.account}</Text>
                    </HStack>
                  </VStack>
                </VStack>
              ) : (
                <Text color="gray.500">No se encontró el retiro</Text>
              )}
            </Dialog.Body>
            <Dialog.Footer borderTop="1px solid #ebecef80" p={4}>
              <Dialog.ActionTrigger asChild>
                <Button> Cerrar </Button>
              </Dialog.ActionTrigger>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
